import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";
import api from "@/lib/api";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function Contact() {
  const { settings } = useSite();
  const s = settings || {};
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    mobile: "",
    preferred_contact: "email",
    service_category: "general",
    additional_info: "",
    consent: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.mobile) {
      toast.error("Please complete name, email and mobile.");
      return;
    }
    if (!form.consent) {
      toast.error("Please accept the privacy policy.");
      return;
    }
    try {
      setSubmitting(true);
      const { data } = await api.post("/enquiries", {
        ...form,
        selected_package: "General enquiry",
      });
      setSent(true);
      toast.success(`Enquiry received. Ref ${data.reference}`);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const whatsappHref = () => {
    const num = (s.whatsapp_number || "").replace(/[^0-9]/g, "");
    const msg = encodeURIComponent(s.whatsapp_message || "Hi Oereshaus, I would like to enquire about a photography session.");
    return `https://wa.me/${num}?text=${msg}`;
  };

  return (
    <main data-testid="contact-page" className="bg-ivory text-ink pt-32 md:pt-40 pb-32">
      <section className="px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-6">
          <p className="label-mono opacity-60 line-badge">Contact</p>
          <h1 className="mt-8 font-editorial text-6xl md:text-8xl leading-[0.95]">
            Say <em className="italic">hello</em>.
          </h1>
          <p className="mt-6 text-lg text-ink/70 max-w-md">
            For bookings, general questions and press. We respond to enquiries within one working day.
          </p>

          <ul className="mt-12 space-y-6">
            <li>
              <p className="label-mono opacity-60">Email</p>
              <a href={`mailto:${s.business_email}`} className="hover-underline text-2xl font-editorial italic" data-testid="contact-email-link">
                {s.business_email}
              </a>
            </li>
            <li>
              <p className="label-mono opacity-60">WhatsApp</p>
              <a href={whatsappHref()} target="_blank" rel="noreferrer" className="hover-underline text-2xl font-editorial italic" data-testid="contact-whatsapp-link">
                {s.whatsapp_number}
              </a>
            </li>
            <li>
              <p className="label-mono opacity-60">Location</p>
              <p className="text-2xl font-editorial italic">{s.location}</p>
            </li>
            <li>
              <p className="label-mono opacity-60">Hours</p>
              <p className="text-lg">{s.business_hours}</p>
            </li>
            <li>
              <p className="label-mono opacity-60">Service Area</p>
              <p className="text-lg">{s.service_area}</p>
            </li>
          </ul>

          <div className="mt-10 flex gap-4 flex-wrap">
            <a href={whatsappHref()} target="_blank" rel="noreferrer" className="btn-outline-ink" data-testid="contact-whatsapp-btn">
              Message on WhatsApp
            </a>
            {s.instagram_url && (
              <a href={s.instagram_url} target="_blank" rel="noreferrer" className="btn-outline-ink" data-testid="contact-instagram-btn">
                Instagram
              </a>
            )}
          </div>
        </div>

        {/* General form */}
        <div className="col-span-12 md:col-span-6 md:pl-12">
          {sent ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-ink/20 p-10"
              data-testid="contact-success"
            >
              <p className="label-mono opacity-60">Received</p>
              <h3 className="mt-4 font-editorial text-4xl leading-tight italic">
                Thank you. Your message is with Oereshaus.
              </h3>
              <p className="mt-4 text-ink/70">
                We will respond by email within one working day.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
              <div>
                <label className="label-mono opacity-60">Full name</label>
                <input
                  className="editorial-input"
                  value={form.full_name}
                  onChange={(e) => update("full_name", e.target.value)}
                  required
                  data-testid="contact-input-name"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label-mono opacity-60">Email</label>
                  <input
                    type="email"
                    className="editorial-input"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    required
                    data-testid="contact-input-email"
                  />
                </div>
                <div>
                  <label className="label-mono opacity-60">Mobile</label>
                  <input
                    className="editorial-input"
                    value={form.mobile}
                    onChange={(e) => update("mobile", e.target.value)}
                    required
                    data-testid="contact-input-mobile"
                  />
                </div>
              </div>
              <div>
                <label className="label-mono opacity-60">Category</label>
                <select
                  className="editorial-select"
                  value={form.service_category}
                  onChange={(e) => update("service_category", e.target.value)}
                  data-testid="contact-input-category"
                >
                  <option value="general">General enquiry</option>
                  <option value="portraits">Portraits</option>
                  <option value="weddings">Weddings</option>
                  <option value="events">Events</option>
                  <option value="matric-balls">Matric Balls</option>
                  <option value="graduations">Graduations</option>
                </select>
              </div>
              <div>
                <label className="label-mono opacity-60">Message</label>
                <textarea
                  rows={5}
                  className="editorial-textarea"
                  value={form.additional_info}
                  onChange={(e) => update("additional_info", e.target.value)}
                  data-testid="contact-input-message"
                />
              </div>
              <label className="flex items-start gap-3 text-sm text-ink/70">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={form.consent}
                  onChange={(e) => update("consent", e.target.checked)}
                  data-testid="contact-input-consent"
                />
                I agree to the privacy policy. My details will be used only to respond to this enquiry.
              </label>
              <button type="submit" disabled={submitting} className="btn-solid" data-testid="contact-submit">
                {submitting ? "Sending…" : "Send message"}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Contact FAQs */}
      <section className="px-6 md:px-12 lg:px-16 mt-32 grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-4">
          <p className="label-mono opacity-60 line-badge">FAQs</p>
          <h2 className="mt-6 font-editorial text-5xl md:text-6xl">Before you ask.</h2>
        </div>
        <div className="col-span-12 md:col-span-8">
          <Accordion type="single" collapsible data-testid="contact-faqs">
            {[
              ["Do you provide outfits or makeup?", "No. Oereshaus does not provide outfits, clothing, wardrobe rental, styling, makeup or hair services. Clients arrange their own."],
              ["Do you travel outside Cape Town?", "Yes. Travel is quoted separately based on distance and time."],
              ["How do I secure my booking?", "A signed agreement and a non-refundable booking retainer secure your date."],
              ["What if it rains?", "We adapt. We can shift indoors, wait it out, or reschedule where possible."],
            ].map(([q, a], i) => (
              <AccordionItem key={i} value={`c-${i}`} className="border-ink/15">
                <AccordionTrigger className="text-left font-editorial text-2xl md:text-3xl py-6 hover:no-underline">
                  {q}
                </AccordionTrigger>
                <AccordionContent className="text-ink/70 leading-relaxed pb-6">
                  {a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </main>
  );
}
